"use client";

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

type DeferUntilVisibleProps = {
  children: ReactNode;
  /** Placeholder height so layout doesn't jump when the real content mounts. */
  minHeight?: number | string;
  /** Start loading slightly before the section enters the viewport. */
  rootMargin?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Defers mounting children until near the viewport.
 * Cuts unused JS / long tasks on initial load for below-fold sections.
 */
export function DeferUntilVisible({
  children,
  minHeight = 480,
  rootMargin = "200px 0px",
  className,
  style,
}: DeferUntilVisibleProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin, visible]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        minHeight: visible ? undefined : minHeight,
        ...style,
      }}
    >
      {visible ? children : null}
    </div>
  );
}
